<template>
  <div class="osync" :class="{ 'osync--collapsed': collapsed }">
    <div class="osync__bar" @click="collapsed = !collapsed">
      <span class="osync__dot" :class="dotClass"></span>
      <strong>Sync offline</strong>
      <span class="osync__count">{{ pending.length }} na fila</span>
      <span class="osync__chev">{{ collapsed ? '▸' : '▾' }}</span>
    </div>

    <div v-show="!collapsed" class="osync__body">
      <div v-if="syncing" class="osync__progress">
        <div class="osync__progress-label">Sincronizando… {{ sync.done }}/{{ sync.total }}</div>
        <div class="osync__track"><div class="osync__fill" :style="{ width: pct + '%' }"></div></div>
      </div>

      <div v-if="!pending.length" class="osync__empty">Fila vazia</div>
      <ul v-else class="osync__list">
        <li v-for="item in pending" :key="item.id" class="osync__item">
          <span class="osync__tag" :class="'osync__tag--' + item.type">{{ tagLabel(item.type) }}</span>
          <span class="osync__desc">{{ describe(item) }}</span>
        </li>
      </ul>

      <div class="osync__actions">
        <button @click="forceSync" :disabled="syncing || !pending.length">Forçar sync</button>
        <button class="osync__ghost" @click="refresh">Atualizar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { offlineEventQueue, OfflineEventType } from '@/services/offlineEventQueue';

export default {
  name: 'OfflineSyncDebug',
  data: () => ({ pending: [], collapsed: false, timer: null }),
  computed: {
    ...mapState('enroll', { sync: (s) => s.offlineSync }),
    syncing () { return Boolean(this.sync && this.sync.active); },
    pct () { return this.sync && this.sync.total ? Math.round((this.sync.done / this.sync.total) * 100) : 0; },
    dotClass () {
      if (this.syncing) return 'is-syncing';
      return this.pending.length ? 'is-pending' : 'is-idle';
    }
  },
  methods: {
    refresh () { this.pending = offlineEventQueue.peekAll(); },
    forceSync () { this.$store.dispatch('enroll/flushOfflineEventQueue'); },
    tagLabel (t) {
      if (t === OfflineEventType.OFFLINE_SESSION) return 'sessão';
      if (t === OfflineEventType.ENROLL_PROGRESS) return 'progresso';
      return t;
    },
    describe (item) {
      const p = item.payload || {};
      if (item.type === OfflineEventType.OFFLINE_SESSION) {
        return `bcast ${p.broadcastId} · ${this.fmtPos(p.position)} · ${this.fmtTime(p.watchedAt)}`;
      }
      if (item.type === OfflineEventType.ENROLL_PROGRESS) {
        return `LO ${p.learningObjectId}${p.complete ? ' · ✓' : ''}`;
      }
      return JSON.stringify(p);
    },
    fmtPos (s) {
      s = Math.round(Number(s) || 0);
      return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    },
    fmtTime (iso) {
      if (!iso) return '';
      try { return new Date(iso).toLocaleTimeString(); } catch { return iso; }
    }
  },
  mounted () {
    this.refresh();
    this.timer = setInterval(this.refresh, 1200);
  },
  beforeDestroy () {
    clearInterval(this.timer);
  }
};
</script>

<style scoped>
.osync {
  position: fixed;
  left: 12px;
  bottom: 12px;
  z-index: 9998;
  width: 280px;
  max-width: calc(100vw - 24px);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  color: #e6edf3;
  background: rgba(13, 17, 23, 0.94);
  border: 1px solid #30363d;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}
.osync__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  cursor: pointer;
  user-select: none;
  background: #161b22;
}
.osync__bar strong { font-weight: 600; }
.osync__dot { width: 8px; height: 8px; border-radius: 50%; flex: none; }
.osync__dot.is-idle { background: #6e7681; }
.osync__dot.is-pending { background: #d29922; }
.osync__dot.is-syncing { background: #2f81f7; animation: osync-pulse 1s ease-in-out infinite; }
@keyframes osync-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
.osync__count { margin-left: auto; color: #8b949e; }
.osync__chev { color: #8b949e; }
.osync__body { padding: 8px 10px; border-top: 1px solid #21262d; }
.osync__progress { margin-bottom: 8px; }
.osync__progress-label { margin-bottom: 4px; color: #58a6ff; }
.osync__track { height: 4px; background: #21262d; border-radius: 2px; overflow: hidden; }
.osync__fill { height: 100%; background: #2f81f7; transition: width 0.2s ease; }
.osync__empty { color: #6e7681; padding: 4px 0; }
.osync__list { list-style: none; margin: 0; padding: 0; max-height: 220px; overflow-y: auto; }
.osync__item { display: flex; align-items: baseline; gap: 6px; padding: 3px 0; border-bottom: 1px solid #21262d; }
.osync__tag {
  flex: none;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.osync__tag--offlineSession { background: #1f6feb33; color: #79c0ff; }
.osync__tag--enrollProgress { background: #23863633; color: #7ee787; }
.osync__desc { color: #c9d1d9; word-break: break-all; }
.osync__actions { display: flex; gap: 6px; margin-top: 8px; }
.osync__actions button {
  flex: 1;
  padding: 5px 8px;
  border: 1px solid #30363d;
  border-radius: 6px;
  background: #238636;
  color: #fff;
  font: inherit;
  cursor: pointer;
}
.osync__actions button:disabled { opacity: 0.4; cursor: default; }
.osync__actions .osync__ghost { background: transparent; color: #c9d1d9; flex: 0 0 auto; }
</style>
